import { useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Plus } from 'blaze-ui/icons/20'
import { RadioGroup } from 'components/RadioGroup/RadioGroup'
import { Radio } from 'components/RadioGroup/Radio'
import { ContentStrategyTypeEmoji } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'
import { ContentStrategyTypeLabel } from '../../../../../../constants'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { collectResults } from 'utils/query'
import { CampaignSelectorProps } from './Types'
import Styles from './CampaignSelector.module.scss'
import { ScheduleStatusPill } from 'screens/Schedule/ScheduleStatusBanner/ScheduleStatusPill'

const PRESET_COUNTS = [1, 2, 3, 4, 5] as const
const INITIAL_VISIBLE_COUNT = 5

export const CampaignSelector = ({ brandPlanId, onConfirm, onBack }: CampaignSelectorProps) => {
  const schedulesQuery = useBrandPlanSchedules({
    brandPlanId,
    start_date: dayjs().format('YYYY-MM-DD'),
    order_direction: 'asc',
    items: 50,
  })
  const schedules = collectResults(schedulesQuery, (data) => data.schedules)
  const sortedSchedules = useMemo(
    () => schedules.sort((a, b) => dayjs(a.start_date).diff(dayjs(b.start_date))),
    [schedules],
  )

  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null)
  const [postCount, setPostCount] = useState(1)
  const [customInputValue, setCustomInputValue] = useState('')
  const [isCustom, setIsCustom] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const { closeModal } = useModals()

  useEffect(() => {
    if (selectedScheduleId === null && sortedSchedules.length > 0) {
      setSelectedScheduleId(sortedSchedules[0].id)
    }
  }, [sortedSchedules, selectedScheduleId])

  const isLoading = schedulesQuery.isLoading
  const selectedSchedule = sortedSchedules.find((s) => s.id === selectedScheduleId)
  const visibleSchedules = showAll ? sortedSchedules : sortedSchedules.slice(0, INITIAL_VISIBLE_COUNT)
  const hasMore = sortedSchedules.length > INITIAL_VISIBLE_COUNT

  const handleSelectSchedule = (value: string) => {
    setSelectedScheduleId(parseInt(value, 10))
  }

  const handlePresetCount = (count: number) => {
    setPostCount(count)
    setIsCustom(false)
  }

  const handleCustomCount = (value: string) => {
    setCustomInputValue(value)
    const num = parseInt(value, 10)
    if (!isNaN(num) && num > 0 && num <= 20) {
      setPostCount(num)
    }
  }

  const handleConfirm = () => {
    if (selectedSchedule) {
      onConfirm(selectedSchedule, postCount)
    }
  }

  return (
    <>
      <Modal.Header title="Create new content" />
      <Modal.Content>
        <div className={Styles.container}>
          <div className={Styles.postCountSection}>
            <Heading level={5}>Number of posts</Heading>
            <div className={Styles.postCountButtons}>
              {PRESET_COUNTS.map((count) => (
                <button
                  key={count}
                  type="button"
                  className={cx(Styles.postCountButton, {
                    [Styles.selected]: postCount === count && !isCustom,
                  })}
                  onClick={() => handlePresetCount(count)}
                >
                  {count}
                </button>
              ))}
              {isCustom ? (
                <input
                  type="text"
                  inputMode="numeric"
                  value={customInputValue}
                  onChange={(e) => handleCustomCount(e.target.value.replace(/[^0-9]/g, ''))}
                  className={Styles.customInputInline}
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  className={cx(Styles.postCountButton)}
                  onClick={() => {
                    setIsCustom(true)
                    setCustomInputValue('10')
                    setPostCount(10)
                  }}
                >
                  Custom
                </button>
              )}
            </div>
          </div>

          <div className={Styles.campaignSection}>
            <span className={Styles.campaignSectionTitle}>Choose a campaign to add the content to</span>
            <div className={Styles.campaignTableHeader}>
              <Text variant="label" color="var(--dark-40)">
                Active campaigns
              </Text>
              <Text variant="label" color="var(--dark-40)">
                Timing
              </Text>
              <Text variant="label" color="var(--dark-40)">
                Status
              </Text>
            </div>
            <div className={Styles.campaignList}>
              {isLoading && sortedSchedules.length === 0 && (
                <>
                  <div className={Styles.skeletonCard} />
                  <div className={Styles.skeletonCard} />
                  <div className={Styles.skeletonCard} />
                </>
              )}
              {!isLoading && sortedSchedules.length === 0 && (
                <div className={Styles.emptyState}>
                  <Text color="var(--dark-40)">No active campaigns found</Text>
                </div>
              )}
              <RadioGroup
                aria-label="Select a campaign"
                value={selectedScheduleId?.toString() ?? ''}
                onChange={handleSelectSchedule}
              >
                {visibleSchedules.map((schedule) => (
                  <Radio
                    key={schedule.id}
                    value={schedule.id.toString()}
                    hideRadio
                    className={Styles.campaignRadio}
                    classNames={{ selected: Styles.selected }}
                  >
                    {() => (
                      <div className={Styles.campaignRowInner}>
                        <div className={Styles.campaignIdentity}>
                          {schedule.cover_image ? (
                            <img src={schedule.cover_image} alt={schedule.title} className={Styles.coverImage} />
                          ) : (
                            <div className={Styles.coverPlaceholder}>
                              <Plus size={16} color="var(--dark-25)" />
                            </div>
                          )}
                          <div className={Styles.campaignInfo}>
                            <Heading level={5} lineClamp={1}>
                              {schedule.title || 'Weekly Batch'}
                            </Heading>
                            {schedule.content_strategy_type && (
                              <Text variant="placeholder" color="var(--dark-40)" lineClamp={1}>
                                <ContentStrategyTypeEmoji
                                  contentStrategyType={schedule.content_strategy_type}
                                  color="var(--dark-100)"
                                />
                                &nbsp;{ContentStrategyTypeLabel[schedule.content_strategy_type]}
                              </Text>
                            )}
                          </div>
                        </div>
                        <Text variant="secondary" color="var(--dark-40)">
                          {dayjs(schedule.start_date).format('ddd, MMM D')} –{' '}
                          {dayjs(schedule.end_date).format('ddd, MMM D')}
                        </Text>
                        <div className={Styles.statusCell}>
                          <ScheduleStatusPill scheduleId={schedule.id} />
                        </div>
                      </div>
                    )}
                  </Radio>
                ))}
              </RadioGroup>
              {hasMore && !showAll && (
                <button type="button" className={Styles.showMoreButton} onClick={() => setShowAll(true)}>
                  <Text color="var(--dark-60)">
                    Show {sortedSchedules.length - INITIAL_VISIBLE_COUNT} more campaigns
                  </Text>
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton size="lg" variant="tertiary" onPress={onBack || closeModal}>
            {onBack ? 'Back' : 'Cancel'}
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton size="lg" onPress={handleConfirm} isDisabled={!selectedSchedule || isLoading}>
            Next, Review Post Topics
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
